import { deDE } from '@clerk/localizations';
import { ClerkProvider, useAuth } from '@clerk/react-router';
import { Fragment, type ReactElement, type ReactNode } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import { CLERK_PUBLISHABLE_KEY } from '../../lib/constants.ts';
import { useImpersonation } from '../../lib/hooks/use-impersonation.ts';
import { i18n } from '../../lib/i18n/i18n.ts';
import { Toaster } from '../ui/toaster.tsx';
import { TooltipProvider } from '../ui/tooltip.tsx';
import { TRPCProvider } from './trpc-provider.tsx';

if (!CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Clerk Publishable Key');
}

export type AppProvidersProps = {
    children?: ReactNode;
};

export function AppProviders({ children }: AppProvidersProps): ReactElement {
    return (
        <HelmetProvider>
            <ClerkProvider
                publishableKey={CLERK_PUBLISHABLE_KEY}
                appearance={{
                    variables: {
                        colorPrimary: '#103FEF',
                    },
                    elements: {
                        footer: 'hidden',
                    },
                }}
                localization={i18n.language === 'de' ? deDE : undefined}
                signInUrl="/sign-in"
            >
                <I18nextProvider i18n={i18n}>
                    <UserProvider>
                        <TRPCProvider>
                            <TooltipProvider>
                                {children}
                                <Toaster />
                            </TooltipProvider>
                        </TRPCProvider>
                    </UserProvider>
                </I18nextProvider>
            </ClerkProvider>
        </HelmetProvider>
    );
}

function UserProvider({ children }: AppProvidersProps): ReactElement {
    const { userId } = useAuth();
    const [impersonatedUserId] = useImpersonation();
    // force children to remount if current logged in identity changes
    return <Fragment key={impersonatedUserId ?? userId}>{children}</Fragment>;
}
